import { useState, useRef, useCallback } from 'react'

export function useAudioPlayer() {
  const [playingId, setPlayingId] = useState<string | null>(null)
  const [progress, setProgress] = useState<Record<string, number>>({})
  const [currentTime, setCurrentTime] = useState<Record<string, string>>({})
  const audioRefs = useRef<Record<string, HTMLAudioElement | null>>({})

  const registerRef = useCallback((id: string, el: HTMLAudioElement | null) => {
    audioRefs.current[id] = el
  }, [])

  const togglePlay = useCallback(
    (id: string) => {
      const audioElement = audioRefs.current[id]
      if (!audioElement) return

      if (playingId === id) {
        audioElement.pause()
        setPlayingId(null)
      } else {
        if (playingId && audioRefs.current[playingId]) {
          audioRefs.current[playingId]?.pause()
        }
        audioElement.play()
        setPlayingId(id)
      }
    },
    [playingId],
  )

  const restartAudio = useCallback(
    (id: string) => {
      const audioElement = audioRefs.current[id]
      if (!audioElement) return

      audioElement.currentTime = 0
      setProgress((prev) => ({ ...prev, [id]: 0 }))
      setCurrentTime((prev) => ({ ...prev, [id]: '0:00' }))

      if (playingId !== id) {
        if (playingId && audioRefs.current[playingId]) {
          audioRefs.current[playingId]?.pause()
        }
        audioElement.play()
        setPlayingId(id)
      }
    },
    [playingId],
  )

  const handleTimeUpdate = useCallback((id: string) => {
    const audioElement = audioRefs.current[id]
    if (!audioElement) return

    const current = audioElement.currentTime
    const duration = audioElement.duration || 1
    const percent = (current / duration) * 100

    setProgress((prev) => ({ ...prev, [id]: percent }))

    const mins = Math.floor(current / 60)
    const secs = Math.floor(current % 60)
    setCurrentTime((prev) => ({
      ...prev,
      [id]: `${mins}:${secs.toString().padStart(2, '0')}`,
    }))
  }, [])

  const handleAudioEnded = useCallback((id: string) => {
    setPlayingId(null)
    setProgress((prev) => ({ ...prev, [id]: 0 }))
    setCurrentTime((prev) => ({ ...prev, [id]: '0:00' }))
  }, [])

  const pauseAll = useCallback(() => {
    if (playingId && audioRefs.current[playingId]) {
      audioRefs.current[playingId]?.pause()
    }
    setPlayingId(null)
  }, [playingId])

  return {
    playingId,
    progress,
    currentTime,
    registerRef,
    togglePlay,
    restartAudio,
    handleTimeUpdate,
    handleAudioEnded,
    pauseAll,
  }
}
